import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  Stack,
  Typography,
  Snackbar,
  useTheme,
} from "@mui/material";
import {
  GridInitialState,
  GridRowSelectionModel,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { useQuery, useMutation } from "@tanstack/react-query";

import { formatDateYYYYMMDD } from "utils/formatting";
import { renderDateCell } from "utils/dataGrid";
import { getDateValue } from "utils/common";
import GaneshDataGrid from "../../components/DataGrid/GaneshDataGrid";
import TableCellBooleanIcon from "../../components/UI/TableCellBooleanIcon";
import useGaneshApi from "../../hooks/useGaneshApi";
import { useDeliveryDialog } from "../../hooks/useDeliveryDialog";

import {
  RiPencilLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiAddCircleLine,
  RiCalendarLine,
  RiErrorWarningLine,
} from "@remixicon/react";

import { Delivery_T } from "../../types/Delivery";
import {
  DeliveryItemT,
  DialogActionMessagesT,
  DialogTypeT,
  DialogTypesT,
} from "../../types/ScheduleForm";

import DeliveriesRemoveDialog from "../../components/Schedule/Dialogs/DeliveriesRemoveDialog";
import DeliveriesDialog from "../../components/Schedule/Dialogs/DeliveriesDialog";

// todo: standardize this
const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: {
    sortModel: [{ field: "target_fulfillment_date", sort: "asc" }],
  },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

export function ScheduleDeliveryPage() {
  const { apiRequest } = useGaneshApi();
  const { delivery_id } = useParams();
  const theme = useTheme();

  const { openDialogType, setOpenDialogType } = useDeliveryDialog() as {
    openDialogType: DialogTypeT;
    setOpenDialogType: (type: DialogTypeT) => void;
  };

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");

  const [data, setData] = useState<DeliveryItemT[] | null>(null);
  const [dataError, setDataError] = useState<boolean>(false);

  const { data: deliveryData, isPending: isDeliveryPending } = useQuery({
    queryKey: ["delivery", delivery_id],
    queryFn: async () => {
      const response = await apiRequest<Delivery_T>(
        `/v1/deliveries/${delivery_id}`
      );
      return response;
    },
  });

  const { mutate: getScheduleByDeliveryId, isPending } = useMutation<
    DeliveryItemT[]
  >({
    mutationFn: async (): Promise<DeliveryItemT[]> => {
      const response = await apiRequest(
        `/v1/deliveries/${delivery_id}/future_schedule`,
        {
          method: "GET",
        }
      );

      return response.data as DeliveryItemT[];
    },
    onSuccess: (response) => {
      setData(response);
    },
    onError: (error: unknown) => {
      console.error(error);
      setDataError(true);
    },
  });

  useEffect(() => {
    getScheduleByDeliveryId();
  }, [delivery_id]);

  useEffect(() => {
    let title = "Delivery";

    if (deliveryData?.data?.id) {
      title += `: ${deliveryData?.data?.customer_name} (${deliveryData?.data?.id})`;
    }

    document.title = title;
  }, [delivery_id, deliveryData]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Delivery Fulfillment ID",
      width: 200,
    },

    {
      field: "target_fulfillment_date",
      headerName: "Target Fulfillment Date",
      width: 200,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },

    {
      field: "is_ad_hoc",
      headerName: "Is Ad-Hoc",
      width: 100,
      renderCell: (params) => <TableCellBooleanIcon value={params.value} />,
    },
    {
      field: "volume_upgrade_scheduled",
      headerName: "Volume Upgrade Scheduled",
      width: 100,
      renderCell: (params) => <TableCellBooleanIcon value={params.value} />,
    },
  ];

  const handleRowSelectionModelChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newRowSelectionModel);
    },
    [setRowSelectionModel]
  );

  const handleCloseChangeDialog = () => {
    setOpenDialogType(DialogTypesT.NONE);
  };

  const handleSuccessCallback = () => {
    const action = openDialogType;

    const message =
      action === DialogTypesT.ADD
        ? DialogActionMessagesT.ADD
        : action === DialogTypesT.CHANGE || action === DialogTypesT.REMOVE
        ? DialogActionMessagesT[action](rowSelectionModel.length)
        : "Success!";

    getScheduleByDeliveryId();
    setData(null);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
    setRowSelectionModel([]);
    setOpenDialogType(DialogTypesT.NONE);
  };

  const handleAddRecurring = () => {
    setOpenDialogType(DialogTypesT.ADD);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const selectedRows = useMemo(() => {
    const filteredRows = data?.filter((row: DeliveryItemT) =>
      rowSelectionModel.includes(row.id)
    );
    return filteredRows?.length ? [filteredRows[0]] : [];
  }, [data, rowSelectionModel]);

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Deliveries Schedule Management
      </Typography>
      <Stack spacing={2}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to={`/shipping/weekly`}>
              Shipping
            </Link>
            <Link color="inherit" to={`/schedule/deliveries`}>
              Deliveries
            </Link>
            <Typography color={theme.palette.text.primary}>
              {delivery_id}
            </Typography>
          </Breadcrumbs>
        </Box>
        {!isDeliveryPending && (
          <Stack spacing={1}>
            <Typography
              variant="h4"
              sx={{ mb: 0, color: theme.palette.text.primary, fontWeight: 600 }}
            >
              {deliveryData?.data?.delivery_name} - {deliveryData?.data?.id}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "", gap: 3 }}>
              {deliveryData?.data?.first_delivery_date && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <RiCalendarLine
                    size={14}
                    style={{
                      color: theme.palette.primary.light,
                      marginRight: 4,
                    }}
                  />
                  <Typography variant="body2">
                    First Delivery:{" "}
                    {formatDateYYYYMMDD(
                      new Date(deliveryData.data.first_delivery_date)
                    )}
                  </Typography>
                </Box>
              )}
              {deliveryData?.data?.final_scheduled_delivery && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <RiCalendarLine
                    size={14}
                    style={{
                      color: theme.palette.primary.light,
                      marginRight: 4,
                    }}
                  />
                  <Typography variant="body2">
                    Last Delivery:{" "}
                    {formatDateYYYYMMDD(
                      new Date(deliveryData.data.final_scheduled_delivery)
                    )}
                  </Typography>
                </Box>
              )}
              {deliveryData?.data?.license_end_date && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <RiCalendarLine
                    size={14}
                    style={{
                      color: theme.palette.primary.light,
                      marginRight: 4,
                    }}
                  />
                  <Typography variant="body2">
                    License End:{" "}
                    {formatDateYYYYMMDD(
                      new Date(deliveryData.data.license_end_date)
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        )}
        {dataError && (
          <Alert
            severity="error"
            icon={
              <RiErrorWarningLine
                size={20}
                style={{ color: "rgb(95, 33, 32)", marginTop: 0.5 }}
              />
            }
          >
            There was an error getting deliveries.
          </Alert>
        )}
        {!dataError && !isDeliveryPending && (
          <Box sx={{ mb: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            {
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={rowSelectionModel.length === 0}
                    onClick={() => setOpenDialogType(DialogTypesT.CHANGE)}
                    aria-label="Change Recurring"
                  >
                    <RiPencilLine size={16} style={{ marginRight: 4 }} /> Change
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={rowSelectionModel.length === 0}
                    onClick={() => setOpenDialogType(DialogTypesT.REMOVE)}
                    aria-label="Remove Recurring"
                  >
                    <RiDeleteBinLine size={16} style={{ marginRight: 4 }} />{" "}
                    Remove
                  </Button>
                </Box>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddRecurring}
                    aria-label="Add Recurring"
                  >
                    <RiAddCircleLine size={16} style={{ marginRight: 4 }} /> Add
                  </Button>
                </Box>
                {openDialogType === DialogTypesT.ADD && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <DeliveriesDialog
                      onClose={handleCloseChangeDialog}
                      onSuccessCallback={handleSuccessCallback}
                      selectedRows={data ? [data[0]] : []}
                      action="ADD_ONE"
                    />
                  </Dialog>
                )}
                {openDialogType === DialogTypesT.CHANGE && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <DeliveriesDialog
                      onClose={handleCloseChangeDialog}
                      selectedRows={selectedRows}
                      action="CHANGE"
                      onSuccessCallback={handleSuccessCallback}
                    />
                  </Dialog>
                )}
                {openDialogType === DialogTypesT.REMOVE && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <DeliveriesRemoveDialog
                      onClose={handleCloseChangeDialog}
                      selectedRows={selectedRows}
                      onSuccessCallback={handleSuccessCallback}
                    />
                  </Dialog>
                )}{" "}
              </Box>
            }
            <GaneshDataGrid
              defaultGridState={defaultGridState}
              gridId="schedule-by-delivery-details"
              loading={isPending}
              data={data || []}
              columns={columns}
              checkboxSelection={true}
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              keepNonExistentRowsSelected
            />
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity="success"
                sx={{ width: "100%" }}
                icon={<RiCheckboxCircleLine size={24} />}
              >
                <Box>
                  <Typography variant="body1">{snackbarMessage}</Typography>
                </Box>
              </Alert>
            </Snackbar>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
